import React, { useState, useEffect, Suspense, lazy, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, TrendingDown, Zap, Globe, BarChart3, Clock, Shield, BookOpen, ArrowDownUp, RefreshCw, Building2, Factory, ChevronUp, Calculator } from 'lucide-react';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { useHybridRates } from '../hooks/useHybridRates';
import { formatRate } from '../utils/format/currency';
import { motion } from 'framer-motion';

import { EconomicNews } from '../components/home/EconomicNews';

const BlogSection = lazy(() => import('../components/blog/BlogSection').then(module => ({ default: module.BlogSection })));

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

const CURRENCY_FLAGS: Record<string, string> = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', CAD: '🇨🇦', AUD: '🇦🇺', XOF: '🌍',
};

const CURRENCY_NAMES: Record<string, string> = {
  USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', CAD: 'Canadian Dollar', AUD: 'Australian Dollar', XOF: 'CFA Franc',
};

function useCountUp(target: number, duration: number = 1500, start: boolean = true) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, start]);

  return value;
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-50 h-11 w-11 rounded-full bg-[#075b49] text-white shadow-lg hover:bg-[#05634f] transition-all flex items-center justify-center"
      aria-label="Scroll to top"
    >
      <ChevronUp className="h-5 w-5" />
    </button>
  );
}

function TrustStrip() {
  const items = [
    { icon: Zap, label: 'Updated every 60 seconds', },
    { icon: Shield, label: 'CBN & parallel market sources', },
    { icon: BarChart3, label: '6 major currencies tracked', },
    { icon: Clock, label: '30 years of historical data', },
  ];

  return (
    <section className="bg-white border-b border-gray-100 py-4 sm:py-5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Icon className="h-4 w-4 text-[#075b49]" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-gray-600 leading-tight">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MiniConverter() {
  const { data: rates } = useExchangeRates();
  const [amount, setAmount] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('USD');

  const currentRate = rates?.find(r => r.fromCurrency === fromCurrency);
  const numericAmount = parseFloat(amount) || 0;
  const convertedAmount = currentRate ? numericAmount * currentRate.rate : 0;

  return (
    <section className="py-10 sm:py-14 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-6 sm:mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <Calculator className="h-5 w-5 text-[#075b49]" />
            <span className="text-xs sm:text-sm font-medium text-[#075b49] uppercase tracking-wider">Quick Convert</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Try the Converter</h2>
          <p className="text-sm text-gray-500 mt-1">Instant conversion at live rates — no signup needed</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="bg-gradient-to-br from-[#f7f8f9] to-emerald-50/50 rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm"
        >
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter amount"
              />
            </div>
            <div className="sm:w-36">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">From</label>
              <select
                value={fromCurrency}
                onChange={(e) => setFromCurrency(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {rates?.map(r => (
                  <option key={r.fromCurrency} value={r.fromCurrency}>{r.fromCurrency}</option>
                )) || (
                  <>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between bg-[#075b49] rounded-xl px-5 py-4">
            <div>
              <p className="text-xs text-emerald-200 uppercase tracking-wider mb-0.5">Result in Naira</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">
                ₦{convertedAmount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-emerald-200 mb-0.5">Rate</p>
              <p className="text-sm font-semibold text-emerald-100">
                ₦{currentRate ? formatRate(currentRate.rate) : '—'}/{fromCurrency}
              </p>
            </div>
          </div>

          <div className="mt-4 text-center">
            <Link
              to="/converter"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#075b49] hover:text-[#05634f] transition-colors"
            >
              Open full converter <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function LiveRateTicker() {
  const { data: rates, isLoading, refetch, isFetching, dataUpdatedAt } = useExchangeRates();

  const featuredRate = rates?.find(r => r.fromCurrency === 'USD');
  const otherRates = rates?.filter(r => r.fromCurrency !== 'USD') ?? [];

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[#f7f8f9]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6 mb-8 sm:mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm font-medium text-emerald-600 uppercase tracking-wider">Live Rates</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Dollar to Naira Today</h2>
            <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2">Real-time parallel market rates, updated every 60 seconds</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Last updated {lastUpdated.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="w-full sm:w-auto flex items-center justify-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 px-4 py-2.5 sm:py-2 rounded-lg border border-emerald-200 hover:bg-emerald-50 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 animate-pulse h-32" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 animate-pulse h-24" />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-5">
            {featuredRate && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="relative bg-gradient-to-br from-[#075b49] via-[#05634f] to-[#064838] rounded-2xl p-6 sm:p-8 shadow-lg overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-400/10 rounded-full blur-3xl" />
                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-3xl sm:text-4xl border border-white/10">
                      {CURRENCY_FLAGS.USD}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">USD / NGN</h3>
                        <span className="text-xs text-emerald-200 bg-emerald-400/20 px-2 py-0.5 rounded-full border border-emerald-300/20">Featured</span>
                      </div>
                      <p className="text-sm text-emerald-200">US Dollar to Nigerian Naira</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 sm:gap-8">
                    <div>
                      <p className="text-xs text-emerald-300 uppercase tracking-wider mb-1">Parallel Rate</p>
                      <p className="text-3xl sm:text-4xl font-bold text-white">
                        ₦{formatRate(featuredRate.rate)}
                      </p>
                    </div>
                    <div className={`flex flex-col items-center px-3 py-2 rounded-xl ${
                      featuredRate.change >= 0 ? 'bg-emerald-400/20' : 'bg-red-400/20'
                    }`}>
                      <div className={`flex items-center gap-1 ${featuredRate.change >= 0 ? 'text-emerald-200' : 'text-red-200'}`}>
                        {featuredRate.change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                        <span className="text-sm font-semibold">{featuredRate.change >= 0 ? '+' : ''}{featuredRate.change.toFixed(2)}%</span>
                      </div>
                      <span className="text-xs text-emerald-300 mt-0.5">24h change</span>
                    </div>
                  </div>
                </div>
                <div className="relative flex items-center gap-1.5 text-xs text-emerald-300 mt-4 sm:mt-5 pt-4 border-t border-emerald-400/20">
                  <Zap className="h-3 w-3 text-emerald-400" />
                  <span>Live updated · Parallel market rate · {lastUpdated ? lastUpdated.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                </div>
              </motion.div>
            )}

            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {otherRates.map(rate => (
                <motion.div
                  key={rate.fromCurrency}
                  variants={fadeUp}
                  className="group bg-white rounded-xl border border-gray-100 p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-lg bg-gray-50 flex items-center justify-center text-lg border border-gray-100">
                        {CURRENCY_FLAGS[rate.fromCurrency] || rate.fromCurrency[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{rate.fromCurrency}/NGN</h3>
                        <p className="text-xs text-gray-400">{CURRENCY_NAMES[rate.fromCurrency] || 'Currency'}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                      rate.change >= 0
                        ? 'text-emerald-700 bg-emerald-50'
                        : 'text-red-700 bg-red-50'
                    }`}>
                      {rate.change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      <span>{rate.change >= 0 ? '+' : ''}{rate.change.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      ₦{formatRate(rate.rate)}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Zap className="h-3 w-3 text-emerald-500" />
                      <span>Live</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}

        <div className="mt-8 sm:mt-10 text-center">
          <Link
            to="/converter"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto text-sm sm:text-base font-semibold text-white bg-[#075b49] hover:bg-[#05634f] transition-colors px-6 py-3 rounded-xl shadow-sm hover:shadow-md"
          >
            View all rates and convert <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function MarketComparison() {
  const { officialRates, parallelRates, isLoading } = useHybridRates(true);

  if (isLoading) return null;

  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'XOF'];
  const comparisonData = currencies.map(code => {
    const official = officialRates?.find(r => r.currency === code)?.centralRate || 0;
    const parallel = parallelRates?.find(r => r.fromCurrency === code)?.rate || 0;
    const spread = parallel > 0 && official > 0 ? ((parallel - official) / official) * 100 : 0;
    return { code, official, parallel, spread };
  }).filter(d => d.official > 0 || d.parallel > 0);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-12 lg:mb-14"
        >
          <span className="text-xs sm:text-sm font-medium text-emerald-600 uppercase tracking-wider">Market Analysis</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 sm:mt-3">Official vs Parallel Rates</h2>
          <p className="text-sm sm:text-base text-gray-500 mt-2 sm:mt-3 max-w-lg mx-auto">Compare CBN official rates with parallel market rates across all tracked currencies</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {comparisonData.map(({ code, official, parallel, spread }) => (
            <motion.div
              key={code}
              variants={scaleIn}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="bg-[#f7f8f9] rounded-xl border border-gray-100 p-4 sm:p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{CURRENCY_FLAGS[code] || ''}</span>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">{code}/NGN</h3>
                </div>
                <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2 sm:px-2.5 py-1 rounded-full whitespace-nowrap">
                  {spread >= 0 ? '+' : ''}{spread.toFixed(1)}%
                </span>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="text-xs sm:text-sm text-blue-700 font-medium">CBN Official</span>
                  <span className="text-base sm:text-lg font-bold text-blue-800">₦{official > 0 ? formatRate(official) : '—'}</span>
                </div>
                <div className="flex items-center justify-between p-3 sm:p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                  <span className="text-xs sm:text-sm text-emerald-700 font-medium">Parallel</span>
                  <span className="text-base sm:text-lg font-bold text-emerald-800">₦{parallel > 0 ? formatRate(parallel) : '—'}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6 max-w-lg mx-auto">
          The spread shows how much the parallel market rate exceeds the CBN official rate. Parallel rates reflect unofficial market activity and may differ by location and provider.
        </p>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: ArrowDownUp,
      title: 'Instant Currency Conversion',
      description: 'Convert between USD, EUR, GBP, CAD, AUD, and CFA with live parallel market rates updated every 60 seconds.',
    },
    {
      icon: BarChart3,
      title: 'Side-by-Side Rate Comparison',
      description: 'See CBN official and parallel market rates next to each other for every currency we track.',
    },
    {
      icon: Shield,
      title: 'Rate Alerts on Your Target',
      description: 'Set custom alerts and get notified the moment your target exchange rate is reached.',
    },
    {
      icon: BookOpen,
      title: 'Free Financial Education Hub',
      description: 'Guides on exchange rates, the Nigerian economy, stock market, Dangote refinery impact, and more.',
    },
    {
      icon: Factory,
      title: 'Economy & Industry Tracking',
      description: 'Follow developments from Dangote refinery, cement, and other sectors that shape the Naira.',
    },
    {
      icon: Building2,
      title: 'Nigerian Stock Exchange News',
      description: 'Stay updated on the NSE, market performance, and major players like Dangote Group.',
    },
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[#f7f8f9]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-12 lg:mb-14"
        >
          <span className="text-xs sm:text-sm font-medium text-emerald-600 uppercase tracking-wider">Features</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 sm:mt-3">Everything you need to track the Naira</h2>
          <p className="text-sm sm:text-base text-gray-500 mt-2 sm:mt-3">Powerful tools for exchange rate tracking and economic insights</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {features.map(({ icon: Icon, title, description }) => (
            <motion.div
              key={title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="group p-6 sm:p-8 bg-white rounded-xl border border-gray-100 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300"
            >
              <div className="inline-flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-emerald-50 text-emerald-600 mb-4 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300 shadow-sm">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#075b49] via-[#05634f] to-[#064838] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `radial-gradient(circle at 20% 50%, rgba(16,185,129,0.4), transparent 50%), radial-gradient(circle at 80% 50%, rgba(52,211,153,0.3), transparent 50%)`,
      }} />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 lg:mb-6"
        >
          Get rate alerts straight to your phone
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-sm sm:text-base lg:text-lg text-emerald-100 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed"
        >
          Set your target exchange rate and get notified the moment the market hits it. Plus real-time Naira rates, market analysis, and instant currency conversion.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center"
        >
          <Link
            to="/converter"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-[#075b49] px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-emerald-50 transition-colors shadow-lg"
          >
            Start Converting <ArrowRight className="h-4 sm:h-5 w-4 sm:w-5" />
          </Link>
          <Link
            to="/signup"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emerald-600/40 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-emerald-600/60 backdrop-blur-sm transition-colors border border-emerald-400/20"
          >
            Create Free Account
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function HeroStats() {
  const stats = [
    { icon: Globe, label: 'Currency Pairs', value: 6, suffix: '+' },
    { icon: BarChart3, label: 'Live Updates', value: 60, suffix: 's' },
    { icon: Clock, label: 'Historical Data', value: 30, suffix: ' Yrs' },
  ];
  const [inView, setInView] = useState(false);

  return (
    <div className="relative">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          onViewportEnter={() => setInView(true)}
          className="grid grid-cols-3 gap-3 sm:gap-5"
        >
          {stats.map(({ icon: Icon, label, value, suffix }) => {
            const animated = useCountUpStatic(value, 1500, inView);
            return (
              <motion.div
                key={label}
                variants={fadeUp}
                className="flex flex-col items-center text-center bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/15 hover:bg-white/15 transition-colors"
              >
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 mb-2 sm:mb-3 border border-white/10">
                  <Icon className="h-5 sm:h-6 w-5 sm:w-6 text-emerald-100" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white tabular-nums">
                  {Math.round(animated)}{suffix}
                </p>
                <p className="text-xs sm:text-sm text-emerald-200/70 mt-0.5">{label}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}

function useCountUpStatic(target: number, duration: number = 1500, start: boolean = true) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, start]);

  return value;
}

export function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#075b49] via-[#05634f] to-[#064838] overflow-hidden">
        {/* Layered premium background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-400/8 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-24 w-96 h-96 bg-teal-300/6 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }} />
          <div className="absolute top-[6%] left-[4%] text-[4rem] sm:text-[6rem] font-bold text-white/[0.05] tracking-tight">$</div>
          <div className="absolute top-[16%] right-[6%] text-[3.5rem] sm:text-[5rem] font-bold text-white/[0.05] tracking-tight">€</div>
          <div className="absolute bottom-[30%] left-[10%] text-[4rem] sm:text-[5.5rem] font-bold text-white/[0.05] tracking-tight">£</div>
          <div className="absolute bottom-[12%] right-[12%] text-[3.5rem] sm:text-[5rem] font-bold text-white/[0.04] tracking-tight">¥</div>
          <div className="absolute top-[40%] left-[38%] text-[5rem] sm:text-[8rem] font-bold text-white/[0.04] tracking-tight">₦</div>
          <div className="absolute top-[28%] right-[32%] text-[3rem] sm:text-[4rem] font-bold text-white/[0.04] tracking-tight">C$</div>
          <div className="absolute bottom-[6%] left-[42%] text-[3.5rem] sm:text-[5rem] font-bold text-white/[0.04] tracking-tight">A$</div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 lg:pt-24 pb-10 sm:pb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-6 sm:mb-8 border border-white/15 shadow-sm">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm font-semibold text-emerald-100 uppercase tracking-wider">Live Exchange Rates</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight max-w-2xl mx-auto tracking-tight">
              Dollar to Naira Today — Live Black Market Rate
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-emerald-100/80 mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto font-light">
              Get real-time Dollar to Naira rates, CBN official rates, and instant currency conversion updated every 60 seconds. Track the Nigerian economy, stock exchange, and Dangote refinery news.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Link
                to="/converter"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-[#075b49] px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl font-semibold hover:bg-emerald-50 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
              >
                Convert Now <ArrowRight className="h-4 sm:h-5 w-4 sm:w-5" />
              </Link>
              <Link
                to="/education-hub"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20 shadow-sm"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Stats bar with count-up animation */}
        <HeroStats />
      </section>

      {/* Trust Strip */}
      <TrustStrip />

      {/* Live Rates */}
      <LiveRateTicker />

      {/* Mini Converter */}
      <MiniConverter />

      {/* Market Comparison */}
      <MarketComparison />

      {/* Economic News */}
      <EconomicNews />

      {/* Features */}
      <FeaturesSection />

      {/* Blog */}
      <Suspense fallback={<div className="py-20 bg-gray-50 text-center text-gray-400">Loading...</div>}>
        <BlogSection />
      </Suspense>

      {/* CTA */}
      <CTASection />

      {/* Scroll to Top */}
      <ScrollToTop />
    </div>
  );
}
